<template>
  <div v-if="relationshipGroups.length" class="work-relationships" :class="{ 'work-relationships--compact': compact }">
    <section v-for="group in relationshipGroups" :key="group.direction" class="work-relationship-group">
      <h4>{{ $t(group.label) }}</h4>
      <div v-for="work in group.works" :key="work.uuid" class="work-relationship-row">
        <span class="work-relationship-link" :title="$t(group.label)"><i class="fas fa-link" aria-hidden="true"></i></span>
        <div class="work-relationship-content">
          <div class="work-relationship-name">{{ work.name || '-' }}</div>
          <div v-if="compact" class="work-relationship-counts">
            <span :title="$t('common.tractor')">
              <img src="@/assets/images/icons/tractor.svg" :alt="$t('common.tractor')" />
              {{ work.machines_count }}
            </span>
            <span :title="$t('common.parcel')">
              <img src="@/assets/images/icons/parcel2.svg" :alt="$t('common.parcel')" />
              {{ work.parcels_count }}
            </span>
          </div>
          <div v-if="!compact" class="work-relationship-context">
            <span v-if="work.machine_name">{{ work.machine_name }}</span>
            <span v-if="work.parcel_name">{{ work.parcel_name }}</span>
            <span v-if="work.work_date">{{ work.work_date }}</span>
          </div>
          <template v-if="!compact">
            <dl class="work-relationship-metrics">
              <div><dt>{{ $t('common.driver') }}</dt><dd>{{ work.person_name || '-' }}</dd></div>
              <div><dt>{{ $t('tools.import_validator.net_surface_worked') }}</dt><dd>{{ formatNumber(work.net_surface_worked) }} ha</dd></div>
              <div v-if="showFuelConsumption"><dt>{{ $t('tools.import_validator.fuel_consumption') }}</dt><dd>{{ formatNumber(work.fuel_consumption) }}</dd></div>
              <div><dt>{{ $t('tools.import_validator.working_time') }}</dt><dd>{{ work.working_time || '-' }}</dd></div>
            </dl>
            <div v-if="work.notes" class="work-relationship-notes">{{ $t('common.notes') }}: {{ work.notes }}</div>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import "@fortawesome/fontawesome-free/css/all.css";

export default {
  name: "WorkRelationships",
  props: {
    relationships: { type: Array, default: () => [] },
    compact: { type: Boolean, default: false },
    showFuelConsumption: { type: Boolean, default: true },
  },
  computed: {
    relationshipGroups() {
      return [
        { direction: "supported_by", label: "tools.special_cases.supporting_works" },
        { direction: "supports", label: "tools.special_cases.serves_works" },
      ].map((group) => {
        const works = new Map();
        this.relationships.filter((relation) => relation.direction === group.direction && relation.work?.uuid)
          .forEach((relation) => works.set(relation.work.uuid, relation.work));
        let rows = Array.from(works.values());
        if (this.compact) {
          const summaries = new Map();
          rows.forEach((work) => {
            const key = work.work_uuid || work.uuid;
            if (!summaries.has(key)) summaries.set(key, { work, machines: new Set(), parcels: new Set() });
            const summary = summaries.get(key);
            if (work.machine_uuid) summary.machines.add(work.machine_uuid);
            if (work.parcel_uuid) summary.parcels.add(work.parcel_uuid);
          });
          rows = Array.from(summaries, ([key, summary]) => ({
            ...summary.work,
            uuid: key,
            machines_count: summary.machines.size,
            parcels_count: summary.parcels.size,
          }));
        }
        return { ...group, works: rows };
      }).filter((group) => group.works.length);
    },
  },
  methods: {
    formatNumber(value) {
      const number = Number(value);
      return value !== null && value !== undefined && Number.isFinite(number) ? number.toFixed(2) : "-";
    },
  },
};
</script>

<style lang="scss" scoped>
.work-relationships {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #cfd9d5;
  color: #203d32;
}
.work-relationship-group + .work-relationship-group { margin-top: 16px; }
.work-relationship-group h4 { margin: 0 0 10px; font-size: 13px; font-weight: 600; }
.work-relationship-row {
  position: relative;
  margin-left: 12px;
  padding: 0 0 14px 28px;
  border-left: 2px solid #23905a;
}
.work-relationship-row:last-child { padding-bottom: 0; }
.work-relationship-row::before {
  content: "";
  position: absolute;
  top: 12px;
  left: 0;
  width: 18px;
  border-top: 2px solid #23905a;
}
.work-relationship-link {
  position: absolute;
  top: 0;
  left: -13px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #087c45;
  color: #fff;
  font-size: 11px;
}
.work-relationship-content { min-width: 0; padding: 3px 0 0; overflow-wrap: anywhere; }
.work-relationship-name { font-size: 13px; font-weight: 600; }
.work-relationship-context { display: flex; flex-wrap: wrap; gap: 4px 16px; color: #64736d; font-size: 12px; }
.work-relationship-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 12px 0 0; }
.work-relationship-metrics dt { color: #64736d; font-size: 12px; font-weight: 400; }
.work-relationship-metrics dd { margin: 3px 0 0; font-size: 13px; }
.work-relationship-notes { margin-top: 10px; font-size: 12px; white-space: pre-wrap; }
.work-relationships--compact {
  margin: 0;
  padding: 10px 20px 12px 42px;
  border-top: 0;
  border-left: 3px solid #23905a;
  background: #f5faf7;
}
.work-relationships--compact .work-relationship-content {
  display: flex;
  align-items: center;
  gap: 16px;
}
.work-relationships--compact .work-relationship-name { flex: 1; min-width: 0; }
.work-relationship-counts { display: flex; flex-shrink: 0; gap: 20px; }
.work-relationship-counts span { display: flex; align-items: center; gap: 6px; min-width: 40px; font-size: 12px; }
.work-relationship-counts img { width: 18px; height: 18px; object-fit: contain; }
@media (max-width: 680px) {
  .work-relationship-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .work-relationships--compact { padding-left: 20px; }
}
</style>